// Figma design: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=25708-104815

import { Button } from '@mui/material';
import { SwipableDialog } from '@tix-ticketing/ui-components';
import { SeatRemoveIcon } from '@tix-ticketing/ui-icons/feature';
import { useIntl } from 'react-intl';
export type ReleaseItemsVariant = 'reservation' | 'subscription';
interface ReleaseItemsDialogProps {
  open: boolean;
  /** What the buyer removed from, which is all the copy speaks in. */
  variant?: ReleaseItemsVariant;
  /** Which button is waiting on the backend, so only that one spins. */
  pendingAction?: 'save' | 'release' | null;
  onClose(): void;
  onSaveAsNewReservation(): void;
  onRelease(): void;
}

/**
 * Asked on checkout when the buyer has removed items from the reservation in
 * the cart. Those items are still held by the reservation at this point, so the
 * buyer decides whether to keep them for later or hand them back.
 */
export const ReleaseItemsDialog = ({
  open,
  variant = 'reservation',
  pendingAction = null,
  onClose,
  onSaveAsNewReservation,
  onRelease
}: ReleaseItemsDialogProps) => {
  const intl = useIntl();
  const pending = pendingAction !== null;

  // A renewal only ever holds seats, so it says so rather than talking about
  // items in general.
  const copy = variant === 'subscription' ? {
    title: intl.formatMessage({
      id: "cart.releaseItems.subscription.title",
      defaultMessage: [{
        "type": 0,
        "value": "Release seats?"
      }]
    }),
    body: intl.formatMessage({
      id: "cart.releaseItems.subscription.body",
      defaultMessage: [{
        "type": 0,
        "value": "You removed seats from your subscription. You can either save the seats for later as a new order or permanently release those seats back to the venue. This action cannot be undone."
      }]
    }),
    release: intl.formatMessage({
      id: "cart.releaseItems.subscription.release",
      defaultMessage: [{
        "type": 0,
        "value": "Release seats"
      }]
    })
  } : {
    title: intl.formatMessage({
      id: "cart.releaseItems.title",
      defaultMessage: [{
        "type": 0,
        "value": "Release items?"
      }]
    }),
    body: intl.formatMessage({
      id: "cart.releaseItems.body",
      defaultMessage: [{
        "type": 0,
        "value": "You removed items from your reservation. You can either save them for later as a new reservation or permanently release them back to the venue. This action cannot be undone."
      }]
    }),
    release: intl.formatMessage({
      id: "cart.releaseItems.release",
      defaultMessage: [{
        "type": 0,
        "value": "Release items"
      }]
    })
  };
  return <SwipableDialog open={open} onClose={onClose} maxWidth="sm" headerIcon={<SeatRemoveIcon />} headerText={copy.title}
  // The body sits centred under the title, which is what the subtitle slot
  // renders — the dialog has no separate subtitle of its own.
  subtitleText={copy.body} secondaryActionButton={<Button data-testid="release-items-save-button" variant="outlined" color="monochrome" size="large" disabled={pending} loading={pendingAction === 'save'} onClick={onSaveAsNewReservation} sx={{
    flex: 1
  }}>
          {intl.formatMessage({
      id: "cart.releaseItems.save",
      defaultMessage: [{
        "type": 0,
        "value": "Save as new order"
      }]
    })}
        </Button>} primaryActionButton={<Button data-testid="release-items-release-button" variant="contained" color="primary" size="large" disabled={pending} loading={pendingAction === 'release'} onClick={onRelease} sx={{
    flex: 1
  }}>
          {copy.release}
        </Button>} />;
};